/**
 * Dashboard Models
 * ================
 *
 * Type definitions for dashboard statistics, charts, and metrics
 *
 * @version 1.0.0
 * @author BeFret Development Team
 */

/**
 * Main dashboard statistics aggregation
 */
export interface DashboardStats {
  totalShipments: number;
  totalRevenue: number;
  averageShipmentValue: number;
  byStatus: StatusCount[];
  byCountry: CountryRevenue[];
  byMonth: MonthlyRevenue[];
  recentShipments: RecentShipment[];
  topDestinations: string[];
}

/**
 * Shipment count grouped by status
 */
export interface StatusCount {
  status: string;
  statusLabel: string;
  count: number;
  percentage: number;
  color: string;
}

/**
 * Revenue metrics by country
 */
export interface CountryRevenue {
  countryCode: string;      // e.g., '+243'
  countryName: string;       // e.g., 'RD Congo'
  revenue: number;
  shipmentCount: number;
  percentage: number;
  barColor: string;
}

/**
 * Monthly revenue trend data
 */
export interface MonthlyRevenue {
  month: string;             // e.g., 'Jan', 'Feb'
  monthNumber: number;       // 1-12
  year: number;
  revenue: number;
  shipmentCount: number;
  percentage: number;
  displayLabel: string;      // e.g., 'Jan 2025'
}

/**
 * Recent shipment summary for quick view
 */
export interface RecentShipment {
  trackingNumber: string;
  senderName: string;
  receiverName: string;
  destination: string;
  status: string;
  statusLabel: string;
  createdAt: Date;
  amount: number;
}

/**
 * Quick action button configuration
 */
export interface QuickAction {
  id: string;
  icon: string;
  labelKey: string;
  route: string;
  queryParams?: any;
  count?: number;
  type: 'primary' | 'secondary' | 'status';
  color?: string;
  badge?: {
    value: number;
    color: string;
  };
}

/**
 * Dashboard filter options
 */
export interface DashboardFilters {
  dateRange?: {
    start: Date;
    end: Date;
  };
  status?: string[];
  countries?: string[];
  minAmount?: number;
  maxAmount?: number;
}

/**
 * Chart configuration for country revenue
 */
export interface CountryChartConfig {
  data: CountryRevenue[];
  maxValue: number;
  showPercentage: boolean;
  colorPalette: string[];
}

/**
 * Chart configuration for monthly revenue
 */
export interface MonthlyChartConfig {
  data: MonthlyRevenue[];
  maxValue: number;
  showTrend: boolean;
  animationDelay: number;
}

/**
 * Status mapping for translations and colors
 */
export const STATUS_CONFIG: { [key: string]: { labelKey: string; color: string } } = {
  'draft': { labelKey: 'STATUS.DRAFT', color: '#6c757d' },
  'pending': { labelKey: 'STATUS.PENDING', color: '#ffc107' },
  'order_confirmed': { labelKey: 'STATUS.ORDER_CONFIRMED', color: '#17a2b8' },
  'collected_europe': { labelKey: 'STATUS.COLLECTED_EUROPE', color: '#007bff' },
  'in_transit': { labelKey: 'STATUS.IN_TRANSIT', color: '#fd7e14' },
  'warehouse_arrival': { labelKey: 'STATUS.WAREHOUSE_ARRIVAL', color: '#6f42c1' },
  'out_for_delivery': { labelKey: 'STATUS.OUT_FOR_DELIVERY', color: '#20c997' },
  'delivered': { labelKey: 'STATUS.DELIVERED', color: '#28a745' },
  'received': { labelKey: 'STATUS.RECEIVED', color: '#28a745' },
  'sent': { labelKey: 'STATUS.SENT', color: '#007bff' },
  'arrived': { labelKey: 'STATUS.ARRIVED', color: '#17a2b8' },
};

/**
 * Country code to name mapping (for display)
 */
export const COUNTRY_NAMES: { [key: string]: string } = {
  '+243': 'RD Congo',
  '+32': 'Belgique',
  '+33': 'France',
  '+31': 'Pays-Bas',
  '+49': 'Allemagne',
  '+44': 'Royaume-Uni',
  '+1': 'États-Unis/Canada',
};

/**
 * Color palette for charts (Befret colors)
 */
export const CHART_COLORS = [
  '#003d89', // Befret Blue
  '#248ac1', // Befret Teal
  '#35bcbf', // Befret Cyan
  '#1e7684', // Befret Dark Teal
  '#54ddb2', // Befret Mint
  '#1f981f', // Befret Green
  '#ffc107', // Warning Yellow
  '#fd7e14', // Orange
  '#6f42c1', // Purple
  '#20c997', // Teal
];

/**
 * Month labels for charts (localized)
 */
export const MONTH_LABELS = [
  'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
  'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'
];
